import ButtonIconT from '@components/tailwind-components/Button/ButtonIconT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { range } from '@conversed/shared/src/helpers/data.helpers';
import clsx from 'clsx';
import type React from 'react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ITimetableProps } from '../../../interfaces/IInputProps';
import TimeInput from './TimeInput';

type TWeekDaysIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const TimetableInput: React.FC<ITimetableProps> = ({
  label,
  watch,
  name,
  control,
  labelClassName = '',
  inputClassName = '',
  dayButtonClassName = '',
}) => {
  const [timetableIndex, setTimeTableIndex] = useState<TWeekDaysIndex>(1);

  const { t } = useTranslation();

  const timeTableValues = watch(name);
  const weekDayNumbers = range(1, 7) as Array<TWeekDaysIndex>;

  return (
    <div>
      <LabelT text={label} className={labelClassName} />
      <div className='mt-1'>
        <div className='flex flex-wrap md:flex-nowrap gap-y-1'>
          {weekDayNumbers.map((weekDayNumber: number) => {
            const isSelected = timetableIndex === weekDayNumber;
            const classes = clsx(
              'px-3',
              'py-2',
              'text-gray-500',
              'text-sm',
              'font-normal',
              'flex-auto',
              'border-r',
              'max-w-[105px]',
              {
                'bg-success text-white': isSelected,
                'bg-gray-200': !isSelected,
                'border-r border-gray-200':
                  weekDayNumber < weekDayNumbers.length,
              },
            );

            return (
              <button
                type='button'
                id={`tbg-btn-${weekDayNumber}`}
                key={weekDayNumber}
                className={clsx(classes, dayButtonClassName)}
                onClick={() =>
                  setTimeTableIndex(weekDayNumber as TWeekDaysIndex)
                }>
                {t(`timetable.weekdays.${weekDayNumber}`)}
              </button>
            );
          })}
        </div>
        <Controller
          control={control}
          name={name}
          render={({ field: { value, onChange } }) => {
            return (
              <div>
                {timeTableValues?.[timetableIndex] ? (
                  <>
                    <div className='flex items-center'>
                      <TimeInput
                        control={control}
                        name={`${name}.${timetableIndex}.start`}
                        watch={watch}
                        label={t('timetable.time.start')}
                        labelClassName={labelClassName}
                        inputClassName={inputClassName}
                      />
                    </div>
                    <div className='flex items-center'>
                      <TimeInput
                        control={control}
                        name={`${name}.${timetableIndex}.end`}
                        watch={watch}
                        label={t('timetable.time.end')}
                        labelClassName={labelClassName}
                        inputClassName={inputClassName}
                      />
                    </div>
                    <ButtonIconT
                      icon='delete'
                      onClick={() => {
                        const changedValue = value;
                        delete changedValue[timetableIndex];
                        return onChange(changedValue);
                      }}
                      label={t(`timetable.weekdays.remove.${timetableIndex}`)}
                      color='danger'
                      className='mt-3'
                      labelClassName='text-xs'
                      iconSize='xl'
                    />
                  </>
                ) : (
                  <ButtonIconT
                    icon='add_circle'
                    onClick={() => {
                      const valueWithFallback = value || {};
                      const newValue = {
                        [timetableIndex]: { start: '', end: '' },
                      };
                      const changedValue = Object.assign(
                        valueWithFallback,
                        newValue,
                      );
                      return onChange(changedValue);
                    }}
                    label={t(`timetable.weekdays.add.${timetableIndex}`)}
                    color='primary-base'
                    className='mt-3'
                    labelClassName='text-xs'
                    iconSize='xl'
                  />
                )}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

export default TimetableInput;
